import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { adminApi, type QuizStatus } from "@/lib/adminApi";

const candidateUrl = (quizId: string) => `${window.location.origin}/quiz/${quizId}`;

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers / http contexts where the Clipboard API is blocked.
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  }
}

export default function AdminQuizzesList() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<QuizStatus | "">("");
  const [page, setPage] = useState(1);

  const list = useQuery({
    queryKey: ["quizzes", search, status, page],
    queryFn: () => adminApi.listQuizzes({
      search: search || undefined,
      status: (status || undefined) as QuizStatus | undefined,
      page, pageSize: 25,
    }),
  });

  const [copiedId, setCopiedId] = useState<string | null>(null);
  const handleCopy = async (quizId: string) => {
    if (await copyToClipboard(candidateUrl(quizId))) {
      setCopiedId(quizId);
      window.setTimeout(() => setCopiedId((c) => (c === quizId ? null : c)), 2000);
    }
  };

  const totalPages = list.data ? Math.max(1, Math.ceil(list.data.totalCount / list.data.pageSize)) : 1;

  return (
    <main className="mx-auto max-w-6xl p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-brand-ink">Quizzes</h1>
        <Link to="/admin/quizzes/new" className="btn-primary">New quiz</Link>
      </div>

      <div className="card mb-4 flex flex-wrap gap-3 p-4">
        <input className="field-input max-w-xs" placeholder="Search title"
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        <select className="field-input max-w-[10rem]" value={status}
          onChange={(e) => { setStatus(e.target.value as QuizStatus | ""); setPage(1); }}>
          <option value="">Any status</option>
          <option>Draft</option><option>Published</option><option>Archived</option>
        </select>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-light text-brand-ink">
            <tr>
              {["Title", "Status", "Pool", "Duration", "Window", "Pool sources", ""].map((h) => (
                <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.data?.items.map((q) => (
              <tr key={q.id} className="border-t border-brand-ink/10">
                <td className="px-3 py-2">
                  <div>{q.title}</div>
                  <div className="font-mono text-[11px] text-brand-ink/50">{q.id}</div>
                </td>
                <td className="px-3 py-2">
                  <span className={`rounded-pill px-2 py-0.5 text-xs
                    ${q.status === "Published" ? "bg-emerald-100 text-emerald-800"
                      : q.status === "Archived" ? "bg-gray-100 text-gray-700"
                      : "bg-yellow-100 text-yellow-800"}`}>
                    {q.status}
                  </span>
                </td>
                <td className="px-3 py-2">{q.poolSize}</td>
                <td className="px-3 py-2">{q.durationMinutes} min</td>
                <td className="px-3 py-2 text-xs">
                  {new Date(q.startAt).toLocaleString()} <br />
                  <span className="text-brand-ink/60">→ {new Date(q.endAt).toLocaleString()}</span>
                </td>
                <td className="px-3 py-2 text-xs">
                  {q.categoryCount} cat · {q.directQuestionCount} direct
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right text-sm">
                  <button
                    className="mr-3 text-brand hover:underline disabled:opacity-50"
                    disabled={q.status !== "Published"}
                    title={q.status !== "Published"
                      ? "Publish the quiz before sharing the link"
                      : candidateUrl(q.id)}
                    onClick={() => handleCopy(q.id)}>
                    {copiedId === q.id ? "Copied!" : "Copy link"}
                  </button>
                  <Link to={`/admin/quizzes/${q.id}`} className="mr-3 text-brand hover:underline">Edit</Link>
                  <Link to={`/admin/quizzes/${q.id}/results`} className="text-brand hover:underline">Results</Link>
                </td>
              </tr>
            ))}
            {list.data?.items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-brand-ink/60">
                No quizzes yet — <Link to="/admin/quizzes/new" className="text-brand hover:underline">create one</Link>.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {list.data && list.data.totalCount > list.data.pageSize && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <div className="text-brand-ink/60">Page {list.data.page} of {totalPages} · {list.data.totalCount} total</div>
          <div className="flex gap-2">
            <button className="btn-secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
            <button className="btn-secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        </div>
      )}
    </main>
  );
}
